import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import * as GamesActions from './games.actions';
import {
  GameFilters,
  GameTypeEnum,
  Game as GamesEntity,
} from '@fantaz/shared/data-access/models';

export const GAMES_FEATURE_KEY = 'games';

export interface GamesState {
  entities: GamesEntity[] | [];
  selectedId?: string | number;
  searchTerm?: string;
  loaded: boolean;
  loadedMore: boolean;
  filtering: boolean;
  allLoaded: boolean;
  filters: GameFilters;
  search: string;
  filterByTags?: string[];
  error?: string | null;
  errorMore?: string | null;
  total: number;
  pageIndex: number;
  pageSize: number;
}

export interface GamesPartialState {
  readonly [GAMES_FEATURE_KEY]: GamesState;
}

export const gamesAdapter: EntityAdapter<GamesEntity> =
  createEntityAdapter<GamesEntity>({
    selectId: (games: GamesEntity) => games.uuid,
  });

export const initialGamesState: GamesState = gamesAdapter.getInitialState({
  loaded: false,
  loadedMore: false,
  allLoaded: false,
  filtering: false,
  total: 0,
  entities: [],
  filters: {
    gameTags: [],
    gameTypes: [GameTypeEnum.CASUAL],
  },
  search: '',
  pageIndex: 0,
  pageSize: 50,
});

const reducer = createReducer(
  initialGamesState,
  on(GamesActions.initGames, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(GamesActions.clearFilterGamesByTag, (state) => ({
    ...state,
    filterByTags: [],
  })),
  on(GamesActions.searchGames, (state, { params }) => ({
    ...state,
    searchTerm: params.search,
    filtering: true,
  })),
  on(GamesActions.searchGamesSuccess, (state, { games }) => ({
    ...state,
    allLoaded: games.items.length === games.total,
    filtering: false,
    entities: games.items,
    pageIndex: games.pageIndex,
    pageSize: games.pageSize,
    filters: games.filters,
    total: games.total,
  })),
  on(GamesActions.clearSearchGames, (state) => ({
    ...state,
    searchTerm: '',
  })),
  on(GamesActions.loadGamesSuccess, (state, { games }) => ({
    ...state,
    loaded: true,
    allLoaded: games.items.length === games.total,
    entities: games.items,
    pageIndex: games.pageIndex,
    pageSize: games.pageSize,
    filters: games.filters,
    total: games.total,
  })),
  on(GamesActions.loadGamesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(GamesActions.loadMoreGames, (state) => ({
    ...state,
    loadedMore: false,
    errorMore: null,
  })),
  on(GamesActions.loadMoreGamesSuccess, (state, { games }) => {
    const entities = [...state.entities, ...games.items];
    return {
      ...state,
      loadedMore: true,
      allLoaded: entities.length === games.total,
      entities,
      pageIndex: games.pageIndex,
      pageSize: games.pageSize,
      filters: games.filters,
      total: games.total,
    };
  }),
  on(GamesActions.loadMoreGamesFailure, (state, { error }) => ({
    ...state,
    errorMore: error,
  })),
  on(GamesActions.filterGamesByTags, (state, { params }) => ({
    ...state,
    filtering: true,
    filterByTags: params.gameTags || [],
    error: null,
  })),
  on(GamesActions.filterGamesByTagsSuccess, (state, { games }) => ({
    ...state,
    filtering: false,
    allLoaded: games.items.length === games.total,
    entities: games.items,
    pageIndex: games.pageIndex,
    pageSize: games.pageSize,
    filters: games.filters,
    total: games.total,
  })),
  on(GamesActions.filterGamesByTagsFailure, (state, { error }) => ({
    ...state,
    filtering: false,
    error: error,
  })),
);

export function gamesReducer(state: GamesState, action: Action) {
  return reducer(state, action);
}
